import { IChartFilter } from '@principle-theorem/reporting/interfaces';
import { AccountCreditDimensionMeasures } from '../../models/dimensions/account-credit-dimension';
import { InvoiceDimensionMeasures } from '../../models/dimensions/invoice-dimension';
import { PatientDimensionMeasures } from '../../models/dimensions/patient-dimension';
import { PatientInteractionDimensionMeasures } from '../../models/dimensions/patient-interaction-dimension';
import {
  CanBeChartedProperty,
  toMeasureBuilderData,
} from '../../models/measure-properties';
import { ChartFilterDefinition } from './chart-filter-definition';
import { ReportBuilderCharts } from './report-builder-charts';

export class ReportBuilderCommonCharts {
  static patient(
    patient: PatientDimensionMeasures,
    sumBy: CanBeChartedProperty
  ): IChartFilter[] {
    const measure = [toMeasureBuilderData(sumBy)];
    return [
      ReportBuilderCharts.predefined(
        'patient.gender',
        patient.gender,
        ChartFilterDefinition.pie('Gender', patient.gender, measure)
      ),
      ReportBuilderCharts.predefined(
        'patient.ageRange',
        patient.ageRange,
        ChartFilterDefinition.row('Age Range', patient.ageRange, measure)
      ),
      ReportBuilderCharts.predefined(
        'patient.status',
        patient.status,
        ChartFilterDefinition.pie('Status', patient.status, measure)
      ),
      ReportBuilderCharts.predefined(
        'patient.referrerType',
        patient.referrerType,
        ChartFilterDefinition.row(
          'Referrer Type',
          patient.referrerType,
          [toMeasureBuilderData(patient.count)],
          {
            groupLimit: 15,
            otherGroupLabel: 'Other',
          }
        )
      ),
      ReportBuilderCharts.predefined(
        'patient.patientReferrers',
        patient.patientReferrers,
        ChartFilterDefinition.selectMenu(
          'Patient Referrers',
          patient.patientReferrers,
          [toMeasureBuilderData(patient.count)]
        )
      ),
      ReportBuilderCharts.predefined(
        'patient.tags',
        patient.tags,
        ChartFilterDefinition.row('Tags', patient.tags, measure, {
          groupLimit: 15,
          otherGroupLabel: 'Other',
        })
      ),
      ReportBuilderCharts.predefined(
        'patient.tags.select',
        patient.tags,
        ChartFilterDefinition.selectMenu('Tags', patient.tags, measure)
      ),
      ReportBuilderCharts.predefined(
        'patient.hasMobileNumber',
        patient.hasMobileNumber,
        ChartFilterDefinition.pie(
          'Has Mobile Number',
          patient.hasMobileNumber,
          [toMeasureBuilderData(patient.count)]
        )
      ),
      ReportBuilderCharts.predefined(
        'patient.hasEmail',
        patient.hasEmail,
        ChartFilterDefinition.pie('Has Email', patient.hasEmail, [
          toMeasureBuilderData(patient.count),
        ])
      ),
      ReportBuilderCharts.predefined(
        'patient.nextVisitBooked',
        patient.nextVisitBooked,
        ChartFilterDefinition.pie(
          'Next Visit Booked',
          patient.nextVisitBooked,
          [toMeasureBuilderData(patient.count)]
        )
      ),
      ReportBuilderCharts.predefined(
        'patient.preferredPractice',
        patient.preferredPractice,
        ChartFilterDefinition.row(
          'Preferred Practice',
          patient.preferredPractice,
          measure,
          { groupLimit: 15, otherGroupLabel: 'Other' }
        )
      ),
      ReportBuilderCharts.predefined(
        'patient.preferredDentist',
        patient.preferredDentist,
        ChartFilterDefinition.row(
          'Preferred Dentist',
          patient.preferredDentist,
          measure,
          { groupLimit: 15, otherGroupLabel: 'Other' }
        )
      ),
      ReportBuilderCharts.predefined(
        'patient.preferredHygienist',
        patient.preferredHygienist,
        ChartFilterDefinition.row(
          'Preferred Hygienist',
          patient.preferredHygienist,
          measure,
          { groupLimit: 15, otherGroupLabel: 'Other' }
        )
      ),
      ReportBuilderCharts.predefined(
        'insurance.hasMedicare',
        patient.hasMedicareCard,
        ChartFilterDefinition.pie(
          'Has Medicare Card',
          patient.hasMedicareCard,
          [toMeasureBuilderData(patient.count)]
        )
      ),
      ReportBuilderCharts.predefined(
        'insurance.hasDVA',
        patient.hasDvaCard,
        ChartFilterDefinition.pie('Has DVA Card', patient.hasDvaCard, [
          toMeasureBuilderData(patient.count),
        ])
      ),
      ReportBuilderCharts.predefined(
        'insurance.hasHealthFund',
        patient.hasHealthFundCard,
        ChartFilterDefinition.pie(
          'Has Health Fund Card',
          patient.hasHealthFundCard,
          [toMeasureBuilderData(patient.count)]
        )
      ),
      ReportBuilderCharts.predefined(
        'insurance.healthFundType.select',
        patient.healthFundType,
        ChartFilterDefinition.selectMenu(
          'Health Fund Type',
          patient.healthFundType,
          [toMeasureBuilderData(patient.count)]
        )
      ),
    ];
  }

  static patientInteraction(
    patientInteraction: PatientInteractionDimensionMeasures,
    sumBy: CanBeChartedProperty
  ): IChartFilter[] {
    const measure = [toMeasureBuilderData(sumBy)];
    return [
      ReportBuilderCharts.predefined(
        'patientInteraction.type',
        patientInteraction.type,
        ChartFilterDefinition.pie('Type', patientInteraction.type, measure)
      ),
      ReportBuilderCharts.predefined(
        'patientInteraction.pinned',
        patientInteraction.pinned,
        ChartFilterDefinition.pie('Pinned', patientInteraction.pinned, measure)
      ),
      ReportBuilderCharts.predefined(
        'patientInteraction.owner',
        patientInteraction.owner,
        ChartFilterDefinition.row('Owner', patientInteraction.owner, measure, {
          groupLimit: 15,
          otherGroupLabel: 'Other',
        })
      ),
      ReportBuilderCharts.predefined(
        'patientInteraction.tags',
        patientInteraction.tags,
        ChartFilterDefinition.row('Tags', patientInteraction.tags, measure, {
          groupLimit: 15,
          otherGroupLabel: 'Other',
        })
      ),
    ];
  }

  static invoice(
    invoice: InvoiceDimensionMeasures,
    sumBy: CanBeChartedProperty
  ): IChartFilter[] {
    const measure = [toMeasureBuilderData(sumBy)];
    return [
      ReportBuilderCharts.predefined(
        'invoice.status',
        invoice.status,
        ChartFilterDefinition.pie('Status', invoice.status, measure)
      ),
      ReportBuilderCharts.predefined(
        'invoice.issuedDate',
        invoice.issuedAt,
        ChartFilterDefinition.columnOverTime(
          'Issued Date',
          invoice.issuedAt,
          measure
        )
      ),
      ReportBuilderCharts.predefined(
        'invoice.paidDate',
        invoice.paidAt,
        ChartFilterDefinition.columnOverTime(
          'Paid Date',
          invoice.paidAt,
          measure
        )
      ),
      ReportBuilderCharts.predefined(
        'invoice.deposits',
        invoice.issuedAt,
        ChartFilterDefinition.columnOverTime('Deposits', invoice.issuedAt, [
          toMeasureBuilderData(invoice.depositTotal),
        ])
      ),
      ReportBuilderCharts.predefined(
        'invoice.productsTotal',
        invoice.issuedAt,
        ChartFilterDefinition.columnOverTime(
          'Products Total',
          invoice.issuedAt,
          [toMeasureBuilderData(invoice.productTotal)]
        )
      ),
      ReportBuilderCharts.predefined(
        'invoice.treatmentsTotal',
        invoice.issuedAt,
        ChartFilterDefinition.columnOverTime(
          'Treatments Total',
          invoice.issuedAt,
          [toMeasureBuilderData(invoice.treatmentTotal)]
        )
      ),
      ReportBuilderCharts.predefined(
        'invoice.products',
        invoice.products,
        ChartFilterDefinition.selectMenu('Products', invoice.products, [
          toMeasureBuilderData(invoice.count),
        ])
      ),
      ReportBuilderCharts.predefined(
        'invoice.treatments',
        invoice.treatments,
        ChartFilterDefinition.selectMenu('Treatments', invoice.treatments, [
          toMeasureBuilderData(invoice.count),
        ])
      ),
      ReportBuilderCharts.predefined(
        'invoice.serviceCodes',
        invoice.serviceCodes,
        ChartFilterDefinition.selectMenu(
          'Service Codes',
          invoice.serviceCodes,
          [toMeasureBuilderData(invoice.count)]
        )
      ),
    ];
  }

  static accountCredit(
    accountCredit: AccountCreditDimensionMeasures,
    sumBy: CanBeChartedProperty
  ): IChartFilter[] {
    return [
      ReportBuilderCharts.predefined(
        'accountCredit.reservedForPractitioner',
        accountCredit.reservedForPractitioner,
        ChartFilterDefinition.column(
          'Reserved For Practitioner',
          accountCredit.reservedForPractitioner,
          [toMeasureBuilderData(sumBy)],
          { rotateXAxisLabels: true }
        )
      ),
    ];
  }
}
